import { Component, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { Button, ButtonVariation } from '../../components/Button';
import { ApplicationProvider } from './context';

import './ApplicationLayout.css';

const MAX_AUTO_RECOVERY_ATTEMPTS = 3;

interface ErrorBoundaryState {
  hasError: boolean;
  errorCount: number;
  shouldShowReload: boolean;
}

/**
 * Error boundary to handle context errors during hot module reload
 * This prevents the app from crashing during development when HMR updates occur
 * After a few failed recovery attempts, shows a reload button to the user
 */
class ApplicationErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { errorCount: 0, hasError: false, shouldShowReload: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const newErrorCount = this.state.errorCount + 1;

    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `ApplicationErrorBoundary caught error (attempt ${newErrorCount}/${MAX_AUTO_RECOVERY_ATTEMPTS}):`,
        error.message,
      );
    }

    // If we've exceeded max attempts, show the reload UI
    if (newErrorCount >= MAX_AUTO_RECOVERY_ATTEMPTS) {
      this.setState({ errorCount: newErrorCount, hasError: true, shouldShowReload: true });

      return;
    }

    // Otherwise, try to auto-recover after a brief moment (allows HMR to complete)
    this.setState({ errorCount: newErrorCount });
    setTimeout(() => {
      this.setState({ hasError: false });
    }, 100);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // Show reload UI after max recovery attempts
    if (this.state.shouldShowReload) {
      return (
        <div
          className='application-error-boundary'
          data-testid='application-error-boundary'>
          <div
            className='application-error-boundary__content'
            data-testid='application-error-boundary-content'>
            <h2
              className='application-error-boundary__heading'
              data-testid='application-error-boundary-title'>
              Something went wrong
            </h2>
            <p
              className='application-error-boundary__message'
              data-testid='application-error-boundary-message'>
              The application encountered an error and couldn&apos;t recover automatically. This can happen during
              development or if there was a problem loading the page.
            </p>
            <Button
              isAutoWidth
              data-testid='application-error-boundary-reload'
              variation={ButtonVariation.SOLID}
              onClick={this.handleReload}>
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    // Show loading state during recovery attempts
    if (this.state.hasError) {
      return (
        <div
          className='application-error-boundary'
          data-testid='application-error-boundary'>
          <div
            className='application-error-boundary__content'
            data-testid='application-error-boundary-content'>
            <p
              className='application-error-boundary__message'
              data-testid='application-error-boundary-message'>
              Loading application...
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ApplicationLayoutProps {
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function ApplicationLayout({ 'data-testid': dataTestId }: ApplicationLayoutProps) {
  const baseTestId = dataTestId ?? 'application-layout';

  return (
    <div data-testid={baseTestId}>
      <ApplicationErrorBoundary>
        <ApplicationProvider>
          <Outlet />
        </ApplicationProvider>
      </ApplicationErrorBoundary>
    </div>
  );
}

export default ApplicationLayout;
